import { isDefined } from "@clipboard-health/util-ts";
import { useWorkerPreferences } from "@src/appV2/Agents/api/useWorkerPreferences";
import { ALL_ROLES_FILTER_VALUE } from "@src/appV2/OpenShifts/components/OpenShiftCalendar/Dialogs/LicenseField";
import { useModalState } from "@src/appV2/redesign/system/ui-components";
import { type Worker } from "@src/appV2/Worker/api/types";
import { useCallback } from "react";

import { PickerButton } from "../../components/PickerButton";
import { useAllRolesFilterContext } from "./hooks/useAllRolesFilter";
import { useEnableDistanceFiltering } from "./hooks/useEnableDistanceFiltering";
import {
  ShiftBlockFilterBottomSheet,
  type ShiftBlockFilterFormValues,
} from "./ShiftBlockFilterBottomSheet";

function getFilterSecondaryText({
  license,
  isAllRolesSelected,
  distance,
  enableDistanceFiltering,
  showDistanceFilter,
}: {
  license: string | undefined;
  isAllRolesSelected: boolean;
  distance: number | undefined;
  enableDistanceFiltering: boolean;
  showDistanceFilter: boolean;
}): string {
  const parts: string[] = [];

  if (isDefined(license) && !isAllRolesSelected) {
    parts.push(license);
  }

  if (showDistanceFilter && isDefined(distance)) {
    parts.push(enableDistanceFiltering ? `${distance}mi` : "Any distance");
  }

  return parts.join("・");
}

interface ShiftBlockFilterProps {
  worker: Worker;
  showDistanceFilter?: boolean;
}

export function ShiftBlockFilter(props: ShiftBlockFilterProps) {
  const { worker, showDistanceFilter = true } = props;

  const filterModalState = useModalState();
  const { filters, setFilters } = useWorkerPreferences();
  const { isAllRolesSelected, setIsAllRolesSelected } = useAllRolesFilterContext();
  const { enableDistanceFiltering, setEnableDistanceFiltering } = useEnableDistanceFiltering();
  const handleSubmit = useCallback(
    (formData: ShiftBlockFilterFormValues) => {
      setEnableDistanceFiltering(formData.enableDistanceFiltering);
      const isAllRoles = formData.license === ALL_ROLES_FILTER_VALUE;
      setIsAllRolesSelected(isAllRoles);
      setFilters({
        license: isAllRoles ? filters?.license : formData.license,
        distance: formData.distance,
      });
    },
    [filters?.license, setFilters, setIsAllRolesSelected, setEnableDistanceFiltering]
  );

  const hasFilters = isDefined(filters);

  const secondaryText = hasFilters
    ? getFilterSecondaryText({
        license: filters.license,
        isAllRolesSelected,
        distance: filters.distance,
        enableDistanceFiltering,
        showDistanceFilter,
      })
    : "";

  return (
    <>
      <PickerButton
        elevated
        centered
        fullWidth
        size="medium"
        aria-label="Filter shift blocks"
        primaryText="Find shift blocks"
        secondaryText={secondaryText}
        startIconType="search"
        isSelected={secondaryText.length > 0}
        sx={(theme) => ({
          borderRadius: theme.borderRadius?.large,
          backgroundColor: theme.palette.background.tertiary,
          border: `1px solid ${theme.palette.border?.primary}`,
          "&:hover": {
            backgroundColor: theme.palette.background.tertiary,
            border: `1px solid ${theme.palette.border?.primary}`,
          },
          "&:active": {
            backgroundColor: theme.palette.background.tertiary,
            border: `1px solid ${theme.palette.border?.primary}`,
          },
        })}
        onClick={() => {
          filterModalState.openModal();
        }}
      />
      {hasFilters && (
        <ShiftBlockFilterBottomSheet
          modalState={filterModalState}
          agentLicenses={worker.licensesData}
          showDistanceFilter={showDistanceFilter}
          defaultValues={
            isAllRolesSelected
              ? { ...filters, license: ALL_ROLES_FILTER_VALUE, enableDistanceFiltering }
              : { ...filters, enableDistanceFiltering }
          }
          onSubmit={handleSubmit}
        />
      )}
    </>
  );
}
